import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { MOBILE_BREAKPOINT } from '../constants/app.constants';
import { CopyDispatchParameters } from '../models/CopyDispatchParameters';
import { Customer } from '../models/Customer';
import { DispatchItem } from '../models/DispatchItem';
import { FormStatus, LocationType } from '../models/Enums';
import { ListItem } from '../models/ListItem';
import { ScheduleItem } from '../models/ScheduleItem';
import { ScheduleParameters } from '../models/ScheduleParameters';
import { DispatchDataService } from '../services/dispatch-data.service';
import { ListDataService } from '../services/list-data.service';
import { ScheduleDataService } from '../services/schedule-data.service';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'copy-dispatch',
    templateUrl: 'copy-dispatch.component.html',
    styles: [],
    standalone: false
})
export class CopyDispatchComponent implements OnInit, OnChanges {
    isShowNotes: boolean;

    constructor(private _listDataService: ListDataService,
        private _scheduleDataService: ScheduleDataService,
        private _dispatchDataService: DispatchDataService,
        private datePipe: DatePipe) {

    }

    @ViewChild('closeButton') closeButton;
    @ViewChild('scheduleSearchDropdown') scheduleSearchDropdownRef: ElementRef;
    @ViewChild('truckerSearchDropdown') truckerSearchDropdownRef: ElementRef;
    @Input() copyDispatchParameters: CopyDispatchParameters = new CopyDispatchParameters();
    @Input() copyDispatchModalId: string = 'copyDispatchModal';
    @Output() saveComplete: EventEmitter<number> = new EventEmitter<number>();

    schedulePeriod: string = '';
    errorMessage: string = '';
    scheduleLocationType: string = '';
    dispatchLocationType: string = '';
    dispatch: DispatchItem = new DispatchItem();
    schedule: ScheduleItem = new ScheduleItem();
    trucker: Customer = new Customer();

    planPeriods: ListItem[] = [];
    scheduleList: ScheduleItem[] = [];
    truckerList: Customer[] = [];

    readonly FormStatus = FormStatus;
    formStatus: FormStatus = FormStatus.Normal;

    isMobile: boolean = false
    showNoSchedules: boolean = false;
    confirmMessage: string = '';
    saveError: string = '';
    scheduleSearchCustomer: string = '';
    scheduleSearchLocation: string = '';
    scheduleSearchCity: string = '';
    filteredScheduleList: ScheduleItem[] = [];
    showScheduleSearch: boolean = false;
    showTruckerSearch: boolean = false;

    getPlanPeriods(): void {
        if (this.copyDispatchParameters.locCode.length == 0) {
            return;
        }

        this._listDataService.getPlanPeriods(this.copyDispatchParameters.locCode).subscribe({
            next: data => {
                this.planPeriods = data;
                if (this.planPeriods.length > 0) {
                    const planPeriodExists = this.planPeriods.some(item => item.value == this.copyDispatchParameters.planPeriod);
                    if (!planPeriodExists) {
                        this.copyDispatchParameters.planPeriod = this.planPeriods[0].value;
                    }
                    this.getPeriodDates(true);
                }
            },
            error: err => this.errorMessage = err
        });
    }

    trackPlanPeriod(index: number, item: ListItem) {
        return item.value;
    }

    getPeriodDates(refreshData: boolean): void {
        this._listDataService.getPeriodDates(this.copyDispatchParameters.planPeriod).subscribe({
            next: data => {
                this.copyDispatchParameters.periodStartDate = data.startDate;
                this.copyDispatchParameters.periodEndDate = data.endDate;
            },
            error: err => this.errorMessage = err
        });
    }

    planPeriodChange() {
        if (this.copyDispatchParameters.planPeriod != 'Custom') {
            this.getPeriodDates(false);
        }
    }

    clearScheduleSearch(): void {
        this.scheduleSearchCustomer = '';
        this.scheduleSearchLocation = '';
        this.scheduleSearchCity = '';
        this.filteredScheduleList = [];
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        const isScheduleSearchDropdown = this.scheduleSearchDropdownRef?.nativeElement?.contains(target);
        const isTruckerSearchDropdown = this.truckerSearchDropdownRef?.nativeElement?.contains(target);
        if (!isScheduleSearchDropdown) {
            this.showScheduleSearch = false;
            this.showNoSchedules = false;
            this.clearScheduleSearch();
        }

        if (!isTruckerSearchDropdown) {
            this.showTruckerSearch = false;
            this.clearTruckerSearch();
        }
    }

    toggleScheduleSearch() {
        if (this.showScheduleSearch) {
            this.showScheduleSearch = false;
            this.showNoSchedules = false;
            this.clearScheduleSearch();
        }
        else {
            this.showScheduleSearch = true;
            this.searchSchedules();
        }
    }

    toggleTruckerSearch() {
        this.showScheduleSearch = false;
        this.showTruckerSearch = !this.showTruckerSearch;
        this.clearTruckerSearch();
    }

    searchSchedulesClick() {
        this.showScheduleSearch = true;
        this.searchSchedules();
        this.clearScheduleSearch();
    }

    searchSchedules() {
        if (this.dispatch.locCode.length == 0) {
            return;
        }

        let searchParms: ScheduleParameters = new ScheduleParameters();
        searchParms.locCode = this.dispatch.locCode;
        searchParms.locationType = this.dispatch.locationType == LocationType.Origin ? LocationType.Destination : LocationType.Origin;
        searchParms.periodStartDate = this.copyDispatchParameters.periodStartDate;
        searchParms.periodEndDate = this.copyDispatchParameters.periodEndDate;
        searchParms.commodityCode = this.dispatch.commodityCode;
        this._scheduleDataService.getSchedulesWithFilters(searchParms).subscribe({
            next: data => {
                //this.scheduleList = data.filter(x => x.scheduleId != this.dispatch.scheduleId);
                this.scheduleList = data;
                this.filteredScheduleList = data;
                this.showNoSchedules = this.scheduleList.length == 0;
            },
            error: err => this.errorMessage = err
        });
    }

    filterSchedules() {
        const customer = this.scheduleSearchCustomer.toLowerCase();
        const location = this.scheduleSearchLocation.toLowerCase();
        const city = this.scheduleSearchCity.toLowerCase();

        this.filteredScheduleList = this.scheduleList.filter(item =>
            (item.customerName?.toLowerCase().includes(customer)) &&
            (item.locationName?.toLowerCase().includes(location)) &&
            (item.locationCity?.toLowerCase().includes(city))
        );
    }

    selectSchedule(schedule: ScheduleItem) {
        this.schedule = { ...schedule };
        this.copyDispatchParameters.scheduleId = schedule.scheduleId;
        this.showScheduleSearch = false;
    }

    truckerSearchForm = this.createTruckerSearchForm();

    createTruckerSearchForm() {
        const truckerSearchForm = new FormGroup({
            truckerName: new FormControl(''),
            truckerCity: new FormControl(''),
            truckerCode: new FormControl('')
        });

        truckerSearchForm.valueChanges
            .pipe(debounceTime(1000))
            .subscribe(() => {
                this.searchTruckers();
            });
        return truckerSearchForm;
    }

    searchTruckers() {
        if (this.truckerSearchForm.controls.truckerName.value.length > 1 ||
            this.truckerSearchForm.controls.truckerCity.value.length > 1 ||
            this.truckerSearchForm.controls.truckerCode.value.length > 1) {
            let searchParms: Customer = new Customer();
            searchParms.customerCode = this.truckerSearchForm.controls.truckerCode.value;
            searchParms.customerName = this.truckerSearchForm.controls.truckerName.value;
            searchParms.city = this.truckerSearchForm.controls.truckerCity.value;
            searchParms.isTrucker = true;
            this._listDataService.searchCustomers(searchParms).subscribe({
                next: data => {
                    this.truckerList = data;
                },
                error: err => this.errorMessage = err
            });
        }
        else {
            this.truckerList = [];
        }
    }

    clearTruckerSearch() {
        this.truckerSearchForm = this.createTruckerSearchForm();
        this.truckerList = [];
    }

    selectTrucker(trucker: Customer): void {
        this.trucker = { ...trucker };
        this.copyDispatchParameters.truckerId = this.trucker.customerId;
        this.showTruckerSearch = false;
    }

    //setTruckerFromDispatch() {
    //    this.trucker = new Customer();
    //    this.trucker.customerId = this.dispatch.truckerId
    //    this.trucker.customerName = this.dispatch.truckerName;
    //    this.trucker.customerCode = this.dispatch.truckerCode;
    //    this.trucker.city = this.dispatch.truckerCity;
    //    this.copyDispatchParameters.truckerId = this.dispatch.truckerId;
    //}


    private saveClick = new Subject<void>();
    private confirmClick = new Subject<void>();

    onSaveClick() {
        this.saveClick.next();
    }
    onConfirmClick() {
        this.confirmClick.next();
    }

    setConfirmMessage(copyParms: CopyDispatchParameters) {
        this.formStatus = FormStatus.ConfirmSave;

        const startDate = this.datePipe.transform(this.copyDispatchParameters.periodStartDate, 'M/d/yyyy') ?? '';
        const endDate = this.datePipe.transform(this.copyDispatchParameters.periodEndDate, 'M/d/yyyy') ?? '';

        if (copyParms.scheduleId > 0 && copyParms.scheduleDispatchCount > 0) {
            this.confirmMessage = `The selected schedule contains ${copyParms.scheduleDispatchCount} `;
            if (copyParms.scheduleDispatchCount > 1) {
                this.confirmMessage += 'dispatches. ';
            }
            else {
                this.confirmMessage += 'dispatch. ';
            }
            this.confirmMessage += 'Do you want to continue?';
        }
        else if (copyParms.scheduleExists) {
            this.confirmMessage = `A matching schedule already exists for the selected period ${startDate}-${endDate}`;
            if (copyParms.scheduleDispatchCount > 0) {
                if (copyParms.scheduleDispatchCount > 1) {
                    this.confirmMessage += ` with ${copyParms.scheduleDispatchCount} dispatches`;
                }
                else {
                    this.confirmMessage += ' with 1 dispatch';
                }
            }
            this.confirmMessage += '.  Do you want to continue and copy the dispatch to the existing schedule? ';
        }
        else if (copyParms.stagingExists) {
            this.confirmMessage = `There is no matching schedule for the selected period ${startDate}-${endDate}.  `;
            this.confirmMessage += 'Do you want to continue and create a schedule from demand staging in the selected period?';
        }
        else {
            this.confirmMessage = `No schedules or demand staging exists that match the schedule for the dispatch in the selected period ${startDate}-${endDate}.  `;
            this.confirmMessage += 'Do you want to continue and create a manual schedule in the selected period?';
        }
    }

    copyDispatch(isConfirmed: boolean): boolean {
        this.showScheduleSearch = false;
        this.showTruckerSearch = false;
        this.saveError = '';
        this.formStatus = FormStatus.Saving;

        this.copyDispatchParameters.isConfirmed = isConfirmed;

        this._dispatchDataService.copyDispatch(this.copyDispatchParameters).subscribe({
            next: copyParms => {
                if (copyParms.saveError) {
                    this.formStatus = FormStatus.Normal;
                    this.saveError = "Error occurred saving the dispatch copy."
                }
                else {
                    if (copyParms.isConfirmed) {
                        if (this.schedulePeriod == this.copyDispatchParameters.periodStartDate) {
                            this.saveComplete.emit(copyParms.scheduleId);
                        }
                        this.closeButton.nativeElement.click();
                    }
                    else {
                        this.setConfirmMessage(copyParms);
                    }
                }
            },
            error: err => {
                this.saveError = err.toString();
                this.formStatus = FormStatus.Normal;
            }
        });

        return false;
    }


    setLocationTypeName() {
        if (this.dispatch.locationType == LocationType.Origin) {
            this.scheduleLocationType = 'Destination';
            this.dispatchLocationType = 'Origin';
        }
        else {
            this.scheduleLocationType = 'Origin';
            this.dispatchLocationType = 'Destination';
        }
    }

    getDispatch(): void {
        if (this.copyDispatchParameters.dispatchId == 0) {
            return;
        }

        this._scheduleDataService.getDispatchItem(this.copyDispatchParameters.dispatchId).subscribe({
            next: data => {
                this.dispatch = data;
                this.setLocationTypeName();
                //this.setTruckerFromDispatch();
                this.searchSchedules();
            },
            error: err => this.errorMessage = err
        });

    }

    setupNewFormState() {
        this.formStatus = FormStatus.Normal;
        this.schedule = new ScheduleItem();
        this.dispatch = new DispatchItem();
        this.trucker = new Customer();
        this.scheduleList = [];
        this.clearTruckerSearch();
        this.showScheduleSearch = false;
        this.showTruckerSearch = false;
        this.showNoSchedules = false;
        this.confirmMessage = '';
        this.saveError = '';
        this.schedulePeriod = this.copyDispatchParameters.periodStartDate;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['copyDispatchParameters']) {
            this.setupNewFormState();
            this.getPlanPeriods();
            this.getDispatch();
        }
    }

    ngOnInit(): void {
        if (window.innerWidth <= MOBILE_BREAKPOINT) {
            this.isMobile = true
        }

        //prevent double click from calling save twice
        this.saveClick.pipe(
            debounceTime(300)
        ).subscribe(() => this.copyDispatch(false));

        this.confirmClick.pipe(
            debounceTime(300)
        ).subscribe(() => this.copyDispatch(true));

    }

} 